import { FunctionComponent, useEffect, useMemo } from 'react';
import TextInputField from '../../Components/TextInput/TextInputField';
import { FormikProps, useFormik } from 'formik';
import Select from '../../Components/Select/Select';
import { ISlotList } from './ISlot';
import { AutocompleteOption } from '../../Components/Select/ISelectProps';
import * as Yup from 'yup';
import DiagramPicker from '../../Components/DiagramPicker/DiagramPicker';

import { useAppDispatch, useAppSelector } from '../../Redux/Store/Hooks';
import { useLocation } from 'react-router-dom';
import {
  clearUpdateSpaceState,
  updateSpace,
} from '../../Redux/Slicers/Space/UpdateSpaceSlicer';
import {
  addSpace,
  clearAddSpaceState,
} from '../../Redux/Slicers/Space/AddSpaceSlicer';

interface SpaceFormProps {
  masterForm: (data: FormikProps<any>) => void;
}

const SlotForm: FunctionComponent<SpaceFormProps> = (props) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { data: ParkingType } = useAppSelector((state) => state?.ParkingType);
  const { spaceupdateableDate } = useAppSelector((state) => state.UpdateSpace);
  const { data: levelsData } = useAppSelector((state: any) => state.Slot);
  const { data: spacesData } = useAppSelector((state: any) => state.Space);
  const { pathname } = location;
  const pathnameParts = location.pathname.split('/');
  const id = pathnameParts[pathnameParts.length - 1];

  const levels: any[] = Array.isArray(levelsData?.response?.data)
    ? levelsData.response.data
    : [];

  console.log('SpaceupdaDate', spaceupdateableDate);

  const masterFrom = useFormik({
    initialValues,
    validationSchema: Yup.object().shape({
      space_id: Yup.string().required('Please enter valid Space Id'),
      zone_id: Yup.string().required('Please enter valid Zone Id'),
      x: Yup.number()
        .min(0, 'x must be a non-negative number')
        .integer('x must be an integer')
        .required('Please enter valid x'),
      y: Yup.number()
        .min(0, 'y must be a non-negative number')
        .integer('y must be an integer')
        .required('Please enter valid y'),
      parking_type: Yup.string().required('Please select a space / parking type'),
    }),
    onSubmit: (value) => {
      if (
        spaceupdateableDate &&
        Object.keys(spaceupdateableDate).length !== 0
      ) {
        console.log(value);

        dispatch(
          updateSpace({
            location_id: id,
            ...value,
            id: spaceupdateableDate._id,
          })
        );
      } else {
        const levelID = localStorage.getItem('level');
        const _fixtureID = localStorage.getItem('fixture');

        dispatch(
          addSpace({
            ...value,
            location_id: id,
            level_id: levelID,
            fixture_id: _fixtureID,
          })
        );
      }
    },
  });

  useEffect(() => {
    props.masterForm(masterFrom);
    if (spaceupdateableDate) {
      masterFrom.setFieldValue('space_id', spaceupdateableDate.space_id);
      masterFrom.setFieldValue('zone_id', spaceupdateableDate.zone_id);
      masterFrom.setFieldValue('location_id', id);
      masterFrom.setFieldValue('level_id', spaceupdateableDate.level_id);
      masterFrom.setFieldValue('fixture_id', spaceupdateableDate.fixture_id);
      masterFrom.setFieldValue('x', spaceupdateableDate.x);
      masterFrom.setFieldValue('y', spaceupdateableDate.y);
      const pt = spaceupdateableDate.parking_type as
        | string
        | { _id?: string; name?: string }
        | undefined;
      const parkingTypeId =
        typeof pt === 'object' && pt != null && pt._id != null
          ? String(pt._id)
          : pt != null && pt !== ''
          ? String(pt)
          : '';
      masterFrom.setFieldValue('parking_type', parkingTypeId);
    }
    return () => {
      dispatch(clearAddSpaceState());
      dispatch(clearUpdateSpaceState());
      masterFrom.resetForm();
    };
  }, [spaceupdateableDate]);

  const data1 = ParkingType?.response?.data;
  if (!data1) {
    console.log('datanot found');
  }

  const projects = Array.isArray(data1)
    ? data1.map((project) => ({
        label: project.name,
        value: String(project._id!),
      }))
    : [];

  const parkingTypeIdForSelect = (() => {
    const pt = masterFrom.values.parking_type as
      | string
      | { _id?: string; name?: string }
      | undefined;
    if (typeof pt === 'object' && pt != null && pt._id != null) {
      return String(pt._id);
    }
    if (pt != null && pt !== '') {
      return String(pt);
    }
    return '';
  })();

  const parkingTypeSelectValue =
    projects.find((p) => p.value === parkingTypeIdForSelect) ?? null;

  const currentLevelId = useMemo(() => {
    const raw =
      (spaceupdateableDate as any)?.level_id?._id ??
      (spaceupdateableDate as any)?.level_id ??
      localStorage.getItem('level');
    return raw ? String(raw) : '';
  }, [spaceupdateableDate]);

  const diagramSrc = useMemo(() => {
    if (!currentLevelId) return null;
    const match = levels.find((lv: any) => String(lv?._id) === currentLevelId);
    const diagram = match?.level_diagram;
    if (typeof diagram !== 'string' || diagram.length === 0) return null;
    return `${process.env.REACT_APP_API_URL}/${diagram}`;
  }, [levels, currentLevelId]);

  const referenceSpaces = useMemo(() => {
    const all = Array.isArray(spacesData?.response?.data)
      ? spacesData.response.data
      : [];
    const currentId = (spaceupdateableDate as any)?._id
      ? String((spaceupdateableDate as any)._id)
      : null;
    const out: { id: string; label: string; x: number; y: number }[] = [];
    for (const s of all) {
      if (!s || !s._id) continue;
      if (currentId && String(s._id) === currentId) continue;
      const sx = Number(s.x);
      const sy = Number(s.y);
      if (!Number.isFinite(sx) || !Number.isFinite(sy)) continue;
      const sLevelId =
        s.level_id?._id != null ? String(s.level_id._id) : String(s.level_id ?? '');
      if (!currentLevelId || sLevelId !== currentLevelId) continue;
      out.push({
        id: String(s._id),
        label: `(${Math.round(sx)}, ${Math.round(sy)})`,
        x: sx,
        y: sy,
      });
    }
    return out;
  }, [spacesData, spaceupdateableDate, currentLevelId]);

  return (
    <form
      className="Slot_form"
      onSubmit={masterFrom.handleSubmit}
      onReset={masterFrom.handleReset}
      autoComplete="off">
      <Select
        id="parking_type"
        name="parking_type"
        label="Space type (parking type)"
        value={parkingTypeSelectValue}
        options={projects}
        onChange={(_, value) => {
          const projectValue = value as AutocompleteOption;
          masterFrom.setFieldValue(
            'parking_type',
            projectValue?.value != null ? String(projectValue.value) : ''
          );
        }}
        onBlur={() => masterFrom.setFieldTouched('parking_type', true)}
        helperText={
          masterFrom.touched.parking_type &&
          typeof masterFrom.errors.parking_type === 'string'
            ? masterFrom.errors.parking_type
            : undefined
        }
        error={
          masterFrom.touched.parking_type &&
          Boolean(masterFrom.errors.parking_type)
        }
        placeholder="-select-"
      />

      <TextInputField
        id="zone_id"
        name="zone_id"
        label="Zone id"
        value={masterFrom.values.zone_id}
        onChange={masterFrom.handleChange}
        onBlur={masterFrom.handleBlur}
        helperText={masterFrom.touched.zone_id && masterFrom.errors.zone_id}
        error={masterFrom.touched.zone_id && Boolean(masterFrom.errors.zone_id)}
      />
      <TextInputField
        id="space_id"
        name="space_id"
        label="Space id "
        value={masterFrom.values.space_id}
        onChange={masterFrom.handleChange}
        onBlur={masterFrom.handleBlur}
        helperText={masterFrom.touched.space_id && masterFrom.errors.space_id}
        error={
          masterFrom.touched.space_id && Boolean(masterFrom.errors.space_id)
        }
      />

      <DiagramPicker
        src={diagramSrc}
        label="Pick space position on level diagram"
        value={{ x: masterFrom.values.x, y: masterFrom.values.y }}
        references={referenceSpaces}
        onPick={({ x, y }) => {
          masterFrom.setFieldValue('x', String(x));
          masterFrom.setFieldValue('y', String(y));
          masterFrom.setFieldTouched('x', true, false);
          masterFrom.setFieldTouched('y', true, false);
        }}
      />

      <div style={{ display: 'flex', gap: 12 }}>
        <TextInputField
          id="x"
          name="x"
          label="X"
          value={masterFrom.values.x}
          onChange={masterFrom.handleChange}
          onBlur={masterFrom.handleBlur}
          helperText={masterFrom.touched.x && masterFrom.errors.x}
          error={masterFrom.touched.x && Boolean(masterFrom.errors.x)}
        />
        <TextInputField
          id="y"
          name="y"
          label="Y"
          value={masterFrom.values.y}
          onChange={masterFrom.handleChange}
          onBlur={masterFrom.handleBlur}
          helperText={masterFrom.touched.y && masterFrom.errors.y}
          error={masterFrom.touched.y && Boolean(masterFrom.errors.y)}
        />
      </div>

      <button
        type="submit"
        style={{ display: 'none' }}
      />
    </form>
  );
};

export default SlotForm;

const initialValues = {
  parking_type: '' as unknown as ISlotList['parking_type'],
  zone_id: '',
  space_id: '',
  x: '' as unknown as ISlotList['x'],
  y: '' as unknown as ISlotList['y'],
} as ISlotList;
